import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { ORDER_NOTIFICATION_CHANNEL } from '@najah/core'
import { api } from './api'

export const supportsPush = Capacitor.getPlatform() === 'android'
const TOKEN_KEY = 'najah.push.token'
let customerId: string | null = null
let generation = 0
let pendingRegistration = Promise.resolve()
let listeners: Promise<void> | null = null
const readStorage = (key: string) => { try { return localStorage.getItem(key) } catch { return null } }
const writeStorage = (key: string, value: string | null) => {
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value) } catch { /* session-only storage */ }
}
let token: string | null = readStorage(TOKEN_KEY)
let requestedGeneration: number | null = null
let registrationTimeout: ReturnType<typeof setTimeout> | undefined
let pendingOrder: { orderId: string; customerId: string } | null = null

export interface PushState { enabled: boolean; busy: boolean; error: string | null }
let state: PushState = { enabled: false, busy: false, error: null }
const subscribers = new Set<() => void>()
export const subscribePush = (fn: () => void) => { subscribers.add(fn); return () => { subscribers.delete(fn) } }
export const getPushState = () => state
export const getCurrentPushToken = () => token
function update(patch: Partial<PushState>) { state = { ...state, ...patch }; subscribers.forEach((fn) => fn()) }
const preferenceKey = (id: string) => `najah.push.enabled.${id}`

export function consumePushOrder(id: string) {
  if (!pendingOrder || pendingOrder.customerId !== id) return null
  const orderId = pendingOrder.orderId
  pendingOrder = null
  return orderId
}

async function setupListeners() {
  listeners ??= (async () => {
    await PushNotifications.addListener('registration', ({ value }) => {
      if (!customerId || requestedGeneration !== generation) return
      clearTimeout(registrationTimeout)
      token = value
      writeStorage(TOKEN_KEY, value)
      const epoch = generation
      const owner = customerId
      pendingRegistration = pendingRegistration.then(async () => {
        if (epoch !== generation) return
        try {
          await api('/push/tokens', { method: 'POST', body: { token: value, platform: 'android' } })
          if (epoch !== generation) return
          writeStorage(preferenceKey(owner), 'true')
          update({ enabled: true, busy: false, error: null })
        } catch {
          if (epoch === generation) update({ busy: false, error: 'تعذّر تفعيل الإشعارات — أعد المحاولة عند الاتصال' })
        }
      })
    })
    await PushNotifications.addListener('registrationError', () => {
      clearTimeout(registrationTimeout)
      update({ busy: false, error: 'تعذّر تسجيل الجهاز للإشعارات' })
    })
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      if (notification.data?.customerId !== customerId) return
      window.dispatchEvent(new CustomEvent('najah:order-update'))
    })
    await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const data = notification.data
      if (data?.type !== 'order-status' || typeof data.orderId !== 'string' || typeof data.customerId !== 'string') return
      pendingOrder = { orderId: data.orderId, customerId: data.customerId }
      window.dispatchEvent(new CustomEvent('najah:open-order'))
    })
  })()
  return listeners
}

export async function connectPush(id: string | null) {
  if (!supportsPush) return
  if (customerId !== id) {
    customerId = id
    generation++
    update({ enabled: false, busy: false, error: null })
  }
  await setupListeners()
  if (id && readStorage(preferenceKey(id)) === 'true') await enablePush(false)
}

export async function enablePush(requestPermission = true) {
  if (!supportsPush || !customerId || state.busy) return
  const epoch = generation
  update({ busy: true, error: null })
  try {
    await setupListeners()
    const server = await api<{ enabled: boolean }>('/push/status')
    if (!server.enabled) throw new Error('الإشعارات غير متاحة حالياً')
    let permission = await PushNotifications.checkPermissions()
    if (requestPermission && (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale')) {
      permission = await PushNotifications.requestPermissions()
    }
    if (epoch !== generation) return
    if (permission.receive !== 'granted') throw new Error('اسمح بالإشعارات من إعدادات الهاتف لتلقّي تحديثات الطلبات')
    await PushNotifications.createChannel({ id: ORDER_NOTIFICATION_CHANNEL, name: 'تحديثات الطلبات', importance: 4, visibility: 0 })
    if (epoch !== generation) return
    requestedGeneration = epoch
    clearTimeout(registrationTimeout)
    registrationTimeout = setTimeout(() => {
      if (epoch === generation) update({ busy: false, error: 'لم يصل تأكيد تسجيل الجهاز — تحقّق من الاتصال وأعد المحاولة' })
    }, 20_000)
    await PushNotifications.register()
  } catch (error) {
    if (epoch === generation) update({ enabled: false, busy: false, error: error instanceof Error ? error.message : 'تعذّر تفعيل الإشعارات' })
  }
}

/** Stop pending registration before logout; also delete the native token when offline. */
export async function disconnectPush(contactServer = true) {
  customerId = null
  generation++
  requestedGeneration = null
  clearTimeout(registrationTimeout)
  pendingOrder = null
  update({ enabled: false, busy: false, error: null })
  if (!supportsPush) return
  // A session-expired callback can run from inside pendingRegistration; avoid waiting on itself.
  if (contactServer) await pendingRegistration
  try {
    if (contactServer && token) await api('/push/tokens', { method: 'DELETE', body: { token } })
  } catch {
    // Native unregister invalidates the token even if the server is unreachable.
  } finally {
    await PushNotifications.unregister().catch(() => undefined)
    await PushNotifications.removeAllDeliveredNotifications().catch(() => undefined)
    token = null
    writeStorage(TOKEN_KEY, null)
  }
}

export async function disablePush() {
  const id = customerId
  if (id) writeStorage(preferenceKey(id), 'false')
  await disconnectPush()
  await connectPush(id)
}
